/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description workflow utils
 */
import _ from 'lodash';

import { getProviderName, getLocationName, getAuthorizedOfficialName } from './admin/npi';
import { PROVIDER_TYPE } from 'app-constants/admin/provider';
import { DEFAULT_TIME_ZONE, FIELD_VALIDATORS } from 'app-constants/index';

const providerIdKeyInMetadata = 'providerId';
const tinKeyInMetadata = 'tin';
const npiKeyInMetadata = 'npi';

/**
 * get provider type
 * @param npiType - npi type
 * @returns {Record<'isProvider' | 'isLocation', boolean>} - provider type
 */
const getNpiMeta = (npiType: Provider.IRegistryType): Record<'isProviderNPI' | 'isLocationNPI', boolean> => {
	return {
		isProviderNPI: npiType === PROVIDER_TYPE.provider.registryCode,
		isLocationNPI: npiType === PROVIDER_TYPE.location.registryCode,
	};
};

// function to get registered name from npi
const getRegisteredName = (npiDetails: Provider.IRegistry): string => {
	const { isProviderNPI, isLocationNPI } = getNpiMeta(npiDetails.enumeration_type);
	if (isProviderNPI) {
		return getProviderName(npiDetails) || '';
	} else if (isLocationNPI) {
		return getLocationName(npiDetails) || '';
	}
	return '';
};

// function to get registered name from npi
const getIdentityVerifierName = (npiDetails: Provider.IRegistry): string => {
	const { isProviderNPI, isLocationNPI } = getNpiMeta(npiDetails.enumeration_type);
	if (isProviderNPI) {
		return getProviderName(npiDetails) || '';
	} else if (isLocationNPI) {
		return getAuthorizedOfficialName(npiDetails) || '';
	}
	return '';
};

// workflowSequences
const workflowSequences = {
	npiVerification: 0,
	identityVerification: 1,
	payerAddition: 2,
};

// workflow steps name
const stepsName = [
	'Verify Organization Details',
	'Add Provider Information',
	'Verify Provider Identity',
	'Add Payer Information',
	'Do your first Eligibility / Claim Status Check',
];

const progressBarSchema = {
	total: 5,
	organizationDetails: 1,
	providerAddition: 2,
	identityVerification: 3,
	ekycCompletion: 3,
	payerAddition: 4,
	completedOnboarding: 5,
	addOnSubscription: 5,
};
// provider options

const getProviderOptions = (providerDetails?: Provider.IPaginatedResponseModel[]): IMultiSelectOption[] => {
	const options = [] as IMultiSelectOption[];

	if (!_.isEmpty(providerDetails)) {
		_.map(providerDetails, ({ name, npi }) => {
			if (npi) {
				options.push({
					label: `${name} (NPI : ${npi})`,
					value: npi,
				});
			}
		});
	}

	return options;
};

// valid tin number

const isValidTin = (tin: string): boolean => {
	return FIELD_VALIDATORS.tin.pattern.test(tin);
};

// restrict user to input only number

const getNumberInput = (input: string): string => {
	return input.replace(/[^\d]/g, '');
};

// restrict user to input only non-negative decimals and numbers

const getDecimalInput = (input: string): string => {
	const sanitized = input.replace(/[^\d.]/g, '');
	const parts = sanitized.split('.');
	return parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : sanitized;
};

// function to prepare npi validation form initial model
const prepareInitialModel = (
	timezone: string,
	billingInfo?: Accounts.IBillingInfoModel,
	metadata?: Record<string, any>,
): Workflow.INpiFormInitialModel => {
	return {
		organizationName: billingInfo?.legal_name || '',
		practiceLocation: billingInfo?.city || '',
		timezone: timezone || DEFAULT_TIME_ZONE,
		tin: _.get(metadata, tinKeyInMetadata, ''),
	};
};

export {
	getNpiMeta,
	providerIdKeyInMetadata,
	tinKeyInMetadata,
	npiKeyInMetadata,
	getRegisteredName,
	getIdentityVerifierName,
	workflowSequences,
	stepsName,
	progressBarSchema,
	getProviderOptions,
	isValidTin,
	getNumberInput,
	getDecimalInput,
	prepareInitialModel,
};
